// 배송비 계산 공통 모듈
// 국내 배송비 산출 로직 (제주 / 도서지역 추가 요금 포함)
// 배송비는 admin > 배송비 설정에서 관리되며, /api/app-settings에서 fetch

import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "";

// 기본값 (DB fetch 실패 시 fallback)
export const DEFAULT_BASE_SHIPPING = 3300;
export const DEFAULT_JEJU_SURCHARGE = 3000;
export const DEFAULT_ISLAND_SURCHARGE = 3300;
export const DEFAULT_INTL_SHIPPING = 11000; // 해외 단일 정액 (전 지역 동일)

// 제주 우편번호 범위
const JEJU_RANGE = [63000, 63644];

// 도서지역 우편번호 대역 (주요)
const ISLAND_PREFIXES = [
  [22386, 22388], [23004, 23010], [23100, 23116], [23124, 23136],
  [28826, 28826], [31708, 31708], [32133, 32133], [33411, 33411],
  [40200, 40240], [46768, 46771], [52570, 52571], [53031, 53033],
  [53088, 53104], [54000, 54000], [56347, 56349], [57068, 57069],
  [58760, 58762], [58800, 58810], [58816, 58818], [58826, 58866],
  [58953, 58958], [59102, 59106], [59127, 59127], [59137, 59166],
  [59421, 59421], [59531, 59531], [59551, 59551], [59563, 59563],
  [59650, 59650], [59766, 59766], [59781, 59790],
];

// 모듈 캐시 — 한 세션 동안 한 번만 fetch
let cachedRates = null;
let cachedPromise = null;

/**
 * 배송비 단가를 서버에서 가져옴 (캐시됨)
 * @returns {Promise<{base: number, jeju: number, island: number}>}
 */
export async function fetchShippingRates() {
  if (cachedRates) return cachedRates;
  if (cachedPromise) return cachedPromise;

  cachedPromise = (async () => {
    try {
      const res = await fetch(`${API}/api/app-settings`);
      const data = await res.json();
      cachedRates = {
        base: parseInt(data.shipping_base) || DEFAULT_BASE_SHIPPING,
        jeju: parseInt(data.shipping_jeju_surcharge) || DEFAULT_JEJU_SURCHARGE,
        island: parseInt(data.shipping_island_surcharge) || DEFAULT_ISLAND_SURCHARGE,
        intl: parseInt(data.shipping_intl) || DEFAULT_INTL_SHIPPING,
      };
    } catch (e) {
      cachedRates = {
        base: DEFAULT_BASE_SHIPPING,
        jeju: DEFAULT_JEJU_SURCHARGE,
        island: DEFAULT_ISLAND_SURCHARGE,
        intl: DEFAULT_INTL_SHIPPING,
      };
    }
    cachedPromise = null;
    return cachedRates;
  })();

  return cachedPromise;
}

/**
 * 우편번호 기반 국내 배송비 계산
 * @param {string} shippingType - "domestic" or "international"
 * @param {string} zipcode - 우편번호
 * @param {{base, jeju, island}} rates - 배송비 단가 (선택, 미지정 시 기본값)
 * @returns {number} 배송비 (원). 해외는 0
 */
export function calcShippingCost(shippingType, zipcode, rates) {
  const r = rates || cachedRates || {
    base: DEFAULT_BASE_SHIPPING,
    jeju: DEFAULT_JEJU_SURCHARGE,
    island: DEFAULT_ISLAND_SURCHARGE,
    intl: DEFAULT_INTL_SHIPPING,
  };

  // 해외 = 단일 정액
  if (shippingType === "international") {
    return r.intl != null ? r.intl : DEFAULT_INTL_SHIPPING;
  }

  const zip = (zipcode || "").replace(/\D/g, "");
  if (!zip) return r.base;

  const z = parseInt(zip);

  // 제주
  if (z >= JEJU_RANGE[0] && z <= JEJU_RANGE[1]) {
    return r.base + r.jeju;
  }

  // 도서지역
  for (const [lo, hi] of ISLAND_PREFIXES) {
    if (z >= lo && z <= hi) return r.base + r.island;
  }

  return r.base;
}

/**
 * 컴포넌트에서 배송비 단가를 가져오는 React Hook
 * @returns {{base, jeju, island}} 단가 (로딩 중이면 기본값)
 */
export function useShippingRates() {
  const [rates, setRates] = useState(
    cachedRates || {
      base: DEFAULT_BASE_SHIPPING,
      jeju: DEFAULT_JEJU_SURCHARGE,
      island: DEFAULT_ISLAND_SURCHARGE,
      intl: DEFAULT_INTL_SHIPPING,
    }
  );

  useEffect(() => {
    let mounted = true;
    fetchShippingRates().then((r) => {
      if (mounted) setRates(r);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return rates;
}
